import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRulerCombined, faBarcode, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import { UnitsAndBarcodesModal } from './UnitsAndBarcodesModal';
import { ChangeIndicator } from '../common/ChangeIndicator';

interface ArticleDetailEinheitenProps {
  data: any;
  mode: 'view' | 'edit';
  onChange: (field: string, value: any) => void;
  isSupplierMode?: boolean;
  originalData?: any;
  articleNumber?: string;
}

const ArticleDetailEinheiten: React.FC<ArticleDetailEinheitenProps> = ({ 
  data, 
  mode, 
  onChange,
  isSupplierMode = false,
  originalData,
  articleNumber
}) => {
  const [showUnitsModal, setShowUnitsModal] = useState(false);

  // **UTILITY FUNCTIONS FOR CHANGE DETECTION**
  const getFieldValue = (obj: any, path: string) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const isFieldChanged = (fieldPath: string): boolean => {
    if (!originalData || !data) return false;
    
    // Use submissionSnapshot as baseline if available, otherwise use originalData
    const baselineData = data?.submissionSnapshot || originalData;
    const baselineValue = getFieldValue(baselineData, fieldPath);
    const currentValue = getFieldValue(data, fieldPath);
    
    // Special handling for units array
    if (fieldPath === 'units') {
      const originalUnits = baselineValue || [];
      const currentUnits = currentValue || [];
      
      // Filter out auto-generated and empty units from comparison
      const filterUnits = (units: any[]) => {
        return units.filter((unit: any) => {
          // Exclude if auto-generated and still empty
          if (unit._isAutoGenerated && unit._isEmpty) {
            // Check if unit is actually still empty (no meaningful data filled)
            const hasFilledData = unit.quantityOfBaseUnits && unit.quantityOfBaseUnits !== '0' && unit.quantityOfBaseUnits !== '' ||
                                 unit.grossWeight && unit.grossWeight !== '' ||
                                 unit.width && unit.width !== '' ||
                                 unit.height && unit.height !== '' ||
                                 unit.depth && unit.depth !== '' ||
                                 unit.packagingUnit && unit.packagingUnit !== '';
            
            return hasFilledData; // Only include if user has filled actual data
          }
          return true; // Include all non-auto-generated units
        });
      };
      
      const filteredOriginal = filterUnits(originalUnits);
      const filteredCurrent = filterUnits(currentUnits);
      
      if (filteredOriginal.length !== filteredCurrent.length) return true;
      return filteredOriginal.some((item, index) => 
        JSON.stringify(item) !== JSON.stringify(filteredCurrent[index])
      );
    }
    
    // Special handling for individual unit changes (e.g., 'units[0]', 'units[1]', etc.)
    if (fieldPath.startsWith('units[')) {
      const currentUnits = getFieldValue(data, 'units') || [];
      const baselineUnits = getFieldValue(baselineData, 'units') || [];
      
      // Extract unit index from fieldPath like 'units[0]'
      const indexMatch = fieldPath.match(/units\[(\d+)\]/);
      if (indexMatch) {
        const unitIndex = parseInt(indexMatch[1]);
        const currentUnit = currentUnits[unitIndex];
        const baselineUnit = baselineUnits[unitIndex];
        
        // If current unit is auto-generated and empty, don't mark as changed
        if (currentUnit?._isAutoGenerated && currentUnit?._isEmpty) {
          const hasFilledData = currentUnit.quantityOfBaseUnits && currentUnit.quantityOfBaseUnits !== '0' && currentUnit.quantityOfBaseUnits !== '' ||
                               currentUnit.grossWeight && currentUnit.grossWeight !== '' ||
                               currentUnit.width && currentUnit.width !== '' ||
                               currentUnit.height && currentUnit.height !== '' ||
                               currentUnit.depth && currentUnit.depth !== '' ||
                               currentUnit.packagingUnit && currentUnit.packagingUnit !== '';
          
          if (!hasFilledData) return false; // Still empty auto-generated unit
        }
        
        // Compare the specific units
        return JSON.stringify(baselineUnit) !== JSON.stringify(currentUnit);
      }
    }
    
    // Special handling for individual barcode changes (e.g., 'barcodes[0]', 'barcodes[1]', etc.)
    if (fieldPath.startsWith('barcodes[')) {
      const currentBarcodes = getFieldValue(data, 'barcodes') || [];
      const baselineBarcodes = getFieldValue(baselineData, 'barcodes') || [];
      
      // Extract barcode index from fieldPath like 'barcodes[0]'
      const indexMatch = fieldPath.match(/barcodes\[(\d+)\]/);
      if (indexMatch) {
        const barcodeIndex = parseInt(indexMatch[1]);
        const currentBarcode = currentBarcodes[barcodeIndex];
        const baselineBarcode = baselineBarcodes[barcodeIndex];
        
        // Compare the specific barcodes
        return JSON.stringify(baselineBarcode) !== JSON.stringify(currentBarcode);
      }
    }
    
    // Simple comparison - any difference from baseline is a change
    if (Array.isArray(baselineValue) && Array.isArray(currentValue)) {
      if (baselineValue.length !== currentValue.length) return true;
      return baselineValue.some((item, index) => 
        JSON.stringify(item) !== JSON.stringify(currentValue[index])
      );
    }
    
    if (typeof baselineValue === 'object' && typeof currentValue === 'object') {
      return JSON.stringify(baselineValue) !== JSON.stringify(currentValue);
    }
    
    return baselineValue !== currentValue;
  };

  const isFieldNew = (fieldPath: string): boolean => {
    if (!originalData || !data) return false;
    
    // IMPORTANT: If a submissionSnapshot exists, the field can NEVER be "new" - only "changed"
    // submissionSnapshot means the supplier has already submitted data before
    const hasSubmissionSnapshot = data?.submissionSnapshot;
    if (hasSubmissionSnapshot) {
      return false; // Never "new" if already submitted before
    }
    
    // Special handling for units array
    if (fieldPath === 'units') {
      const originalUnits = getFieldValue(originalData, fieldPath) || [];
      const currentUnits = getFieldValue(data, fieldPath) || [];
      
      // Check if there are new meaningful units (not auto-generated empty ones)
      const hasNewMeaningfulUnits = currentUnits.some((unit: any) => {
        // Skip auto-generated units that are still empty
        if (unit._isAutoGenerated && unit._isEmpty) {
          const hasFilledData = unit.quantityOfBaseUnits && unit.quantityOfBaseUnits !== '0' && unit.quantityOfBaseUnits !== '' ||
                               unit.grossWeight && unit.grossWeight !== '' ||
                               unit.width && unit.width !== '' ||
                               unit.height && unit.height !== '' ||
                               unit.depth && unit.depth !== '' ||
                               unit.packagingUnit && unit.packagingUnit !== '';
          return hasFilledData; // Only consider it "new" if user filled actual data
        }
        return true; // Non-auto-generated units are always considered meaningful
      });
      
      return originalUnits.length === 0 && hasNewMeaningfulUnits;
    }
    
    // Special handling for individual unit changes (e.g., 'units[0]', 'units[1]', etc.)
    if (fieldPath.startsWith('units[')) {
      const currentUnits = getFieldValue(data, 'units') || [];
      const originalUnits = getFieldValue(originalData, 'units') || [];
      
      // Extract unit index from fieldPath like 'units[0]'
      const indexMatch = fieldPath.match(/units\[(\d+)\]/);
      if (indexMatch) {
        const unitIndex = parseInt(indexMatch[1]);
        const currentUnit = currentUnits[unitIndex];
        const originalUnit = originalUnits[unitIndex];
        
        // If current unit is auto-generated and empty, don't mark as new
        if (currentUnit?._isAutoGenerated && currentUnit?._isEmpty) {
          const hasFilledData = currentUnit.quantityOfBaseUnits && currentUnit.quantityOfBaseUnits !== '0' && currentUnit.quantityOfBaseUnits !== '' ||
                               currentUnit.grossWeight && currentUnit.grossWeight !== '' ||
                               currentUnit.width && currentUnit.width !== '' ||
                               currentUnit.height && currentUnit.height !== '' ||
                               currentUnit.depth && currentUnit.depth !== '' ||
                               currentUnit.packagingUnit && currentUnit.packagingUnit !== '';
          
          if (!hasFilledData) return false; // Still empty auto-generated unit
        }
        
        // Field is "new" if original was empty/null and current has a value
        const originalEmpty = !originalUnit || Object.keys(originalUnit).length === 0;
        const currentHasValue = currentUnit && Object.keys(currentUnit).length > 0;
        
        return originalEmpty && currentHasValue;
      }
    }
    
    // Special handling for individual barcode changes (e.g., 'barcodes[0]', 'barcodes[1]', etc.)
    if (fieldPath.startsWith('barcodes[')) {
      const currentBarcodes = getFieldValue(data, 'barcodes') || [];
      const originalBarcodes = getFieldValue(originalData, 'barcodes') || [];
      
      // Extract barcode index from fieldPath like 'barcodes[0]'
      const indexMatch = fieldPath.match(/barcodes\[(\d+)\]/);
      if (indexMatch) {
        const barcodeIndex = parseInt(indexMatch[1]);
        const currentBarcode = currentBarcodes[barcodeIndex];
        const originalBarcode = originalBarcodes[barcodeIndex];
        
        // Field is "new" if original was empty/null and current has a value
        const originalEmpty = !originalBarcode || Object.keys(originalBarcode).length === 0;
        const currentHasValue = currentBarcode && Object.keys(currentBarcode).length > 0;
        
        return originalEmpty && currentHasValue;
      }
    }
    
    // Only check if truly new (first time filling) when no snapshot exists
    const baselineValue = getFieldValue(originalData, fieldPath);
    const currentValue = getFieldValue(data, fieldPath);
    
    const baselineEmpty = !baselineValue || baselineValue === '' || baselineValue === null || 
                         (Array.isArray(baselineValue) && baselineValue.length === 0);
    const currentHasValue = currentValue && currentValue !== '' && currentValue !== null && 
                           (!Array.isArray(currentValue) || currentValue.length > 0);
    
    // Field is "new" (green) only if it's the very first time being filled
    return baselineEmpty && currentHasValue;
  };

  // **ENHANCED UTILITY FUNCTION FOR RENDERING FIELDS WITH CHANGE HIGHLIGHTING**
  const renderFieldWithHistory = (
    fieldName: string, 
    fieldPath: string,
    content: React.ReactNode
  ) => {
    // Debug-Ausgaben
    console.log(`[EINHEITEN_RENDER_FIELD_WITH_HISTORY] ${fieldName}:`, {
      isSupplierMode,
      hasOriginalData: !!originalData,
      hasArticleNumber: !!articleNumber,
      fieldPath,
      mode
    });
    
    // Change tracking only for Admin (not Supplier mode) AND only in view mode
    if (isSupplierMode || !originalData || !articleNumber || mode === 'edit') {
      console.log(`[EINHEITEN_RENDER_FIELD_WITH_HISTORY] ${fieldName}: Returning plain content (isSupplierMode: ${isSupplierMode}, mode: ${mode})`);
      return content;
    }
    
    const hasChanges = isFieldChanged(fieldPath);
    const isNew = isFieldNew(fieldPath);
    
    console.log(`[EINHEITEN_CHANGE_INDICATOR] ${fieldName}:`, {
      hasChanges,
      isNew,
      fieldPath,
      articleNumber,
      mode
    });
    
    return (
      <ChangeIndicator
        fieldName={fieldName}
        fieldPath={fieldPath}
        hasChanges={hasChanges}
        isNew={isNew}
        articleNumber={articleNumber}
        className="overflow-visible"
        positionOutside={true}
      >
        {content}
      </ChangeIndicator>
    );
  };

  return (
    <div className="space-y-6">
      {/* **EINHEITEN UND BARCODES - KOMPAKTE ANSICHT** */}
      
      {/* Einheiten - Kompakt */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Artikeleinheiten</h4>
          {mode === 'edit' && (
            <button
              type="button"
              onClick={() => setShowUnitsModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Bearbeiten
            </button>
          )}
        </div>
        
        <div className="space-y-2">
          {data?.units && data.units.length > 0 ? (
            data.units.map((unit: any, index: number) => 
              renderFieldWithHistory(`Einheit ${index + 1}`, `units[${index}]`, (
                <div key={unit.id || index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <div className="flex-1">
                    <span className="font-medium text-sm">{unit.unitDescriptor || 'Unbekannt'}</span>
                    <span className="text-gray-500 text-xs ml-2">({unit.packagingUnit || 'N/A'})</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {unit.quantityOfBaseUnits && `${unit.quantityOfBaseUnits} Stk.`}
                  </div>
                </div>
              ))
            )
          ) : (
            <p className="text-gray-500 text-sm">Keine Einheiten definiert</p>
          )}
        </div>
      </div>

      {/* Barcodes - Kompakt */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Barcodes</h4>
          {mode === 'edit' && (
            <button
              type="button"
              onClick={() => setShowUnitsModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Bearbeiten
            </button>
          )}
        </div>
        
        <div className="space-y-2">
          {data?.barcodes && data.barcodes.length > 0 ? (
            <>
              {data.barcodes.slice(0, 3).map((barcode: any, index: number) => 
                renderFieldWithHistory(`Barcode ${index + 1}`, `barcodes[${index}]`, (
                  <div key={barcode.id || index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <div className="flex-1">
                      <span className="font-mono text-sm">{barcode.gtin || 'Kein GTIN'}</span>
                      {barcode.isMainBarcode && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Haupt
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {barcode.unitDescriptor || 'Keine Einheit'}
                    </div>
                  </div>
                ))
              )}
              {data.barcodes.length > 3 && (
                <p className="text-xs text-gray-500 text-center">+{data.barcodes.length - 3} weitere...</p>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-sm">Keine Barcodes definiert</p>
          )}
        </div>
      </div>

      {/* **EINHEITEN UND BARCODES MODAL** */}
      <UnitsAndBarcodesModal
        isOpen={showUnitsModal}
        onClose={() => setShowUnitsModal(false)}
        data={data}
        mode={mode}
        onChange={onChange}
        isSupplierMode={isSupplierMode}
        originalData={originalData}
        articleNumber={articleNumber}
      />
    </div>
  );
};

export default ArticleDetailEinheiten;
